import { createReducer, on } from '@ngrx/store';
import {
  loadCaptureSessions,
  loadCaptureSessionsSuccess,
  loadCaptureSessionsFailure,
  addCaptureSession,
  addCaptureSessionSuccess,
  addCaptureSessionFailure,
  clearCaptureSessions,
} from './capture-sessions.actions';
import * as CapturesActions from '../captures/captures.actions';
import { CaptureSessionsState } from '../../interfaces/state/reducers/capture-sessions-state.interface';
import { CaptureSession } from '../../interfaces/state/entities/capture-session.interface';

const initialState: CaptureSessionsState = {
  sessions: [],
  loading: false,
  error: null,
};

/** Preserve display fields when a reload returns sparse session DTOs from the API. */
export function mergeCaptureSessionWithPrevious(
  incoming: CaptureSession,
  previous?: CaptureSession
): CaptureSession {
  if (!previous) {
    return incoming;
  }
  return {
    ...incoming,
    name: incoming.name || previous.name,
    camera: incoming.camera || previous.camera,
    device: incoming.device || previous.device,
    startedAt: incoming.startedAt ?? previous.startedAt,
    drawingRevision: incoming.drawingRevision ?? previous.drawingRevision,
    captureCount: Math.max(incoming.captureCount, previous.captureCount),
  };
}

export const captureSessionsReducer = createReducer(
  initialState,
  on(loadCaptureSessions, (state) => ({ ...state, loading: true, error: null })),
  on(loadCaptureSessionsSuccess, (state, { sessions }) => ({
    ...state,
    sessions: sessions.map((incoming) =>
      mergeCaptureSessionWithPrevious(
        incoming,
        state.sessions.find((s) => s.id === incoming.id)
      )
    ),
    loading: false,
  })),
  on(loadCaptureSessionsFailure, (state, { error }) => ({
    ...state,
    loading: false,
    error,
  })),
  on(addCaptureSession, (state) => ({ ...state, loading: true, error: null })),
  on(addCaptureSessionSuccess, (state, { session }) => ({
    ...state,
    sessions: [...state.sessions, session],
    loading: false,
  })),
  on(addCaptureSessionFailure, (state) => ({
    ...state,
    loading: false,
    error: null,
  })),
  on(CapturesActions.addCaptureSuccess, (state, { capture }) => ({
    ...state,
    sessions: state.sessions.map((s) =>
      s.id === capture.sessionId ? { ...s, captureCount: s.captureCount + 1 } : s
    ),
  })),
  on(clearCaptureSessions, () => initialState)
);
